import React, { useEffect, useRef } from "react";
import {
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  List,
  ListItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import styles from "./agent-timeline.module.css";

export interface SourceLink {
  url: string;
}

export interface QueryBlock {
  title: string;
  code: string;
}

export interface TimelineStep {
  title: string;
  subtitle?: string;
  time?: string;
  actions?: string[];
  query?: QueryBlock;
  sources?: SourceLink[];
}

export interface AgentTimelineProps {
  timeline: TimelineStep[];
}

const AgentTimeline = ({ timeline }: AgentTimelineProps) => {
  const timelineEndRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.agentTimeline}>
      <div className={styles.timelineHeader}>
        <h3>Agent Timeline</h3>
        <p>Detailed timeline of actions taken by the research agent.</p>
      </div>
      <div className={styles.actionsHeader}>
        <h5>Actions</h5>
        <Chip
          classes={{
            root: styles.chipRoot,
            label: styles.chipLabel,
          }}
          label={
            <div className={styles.completedChip}>
              <CheckCircleOutlineRoundedIcon fontSize="small" />
              <span>Research Completed</span>
            </div>
          }
          size="small"
        />
      </div>

      <List disablePadding>
        {timeline.map((step, index) => (
          <ListItem key={index} className={styles.listItem}>
            {/* Step Header */}
            <div className={styles.stepHeader}>
              <CheckCircleOutlineRoundedIcon fontSize="small" />
              <h6>{step.title}</h6>
              {step.time && <div className={styles.stepTime}>{step.time}</div>}
            </div>

            {/* Subtitle */}
            {step.subtitle && (
              <div className={styles.subtitle}>{step.subtitle}</div>
            )}

            {/* Sub-actions */}
            {step.actions && (
              <ul className={styles.subActions}>
                {step.actions.map((action, i) => (
                  <li key={i}>{action}</li>
                ))}
              </ul>
            )}

            {/* Query Section */}
            {step.query && (
              <Accordion
                variant="outlined"
                defaultExpanded
                className={styles.queryAccordion}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <h6>{step.query.title}</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="pre">{step.query.code}</Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Sources */}
            {step.sources && step.sources.length > 0 && (
              <div className={styles.sources}>
                <h6>Sources found</h6>
                {step.sources.map((src, i) => (
                  <Link
                    key={i}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    className={styles.sourceLink}
                    title={src.url}
                  >
                    <LanguageRoundedIcon fontSize="small" />
                    <span>{src.url}</span>
                  </Link>
                ))}
              </div>
            )}
          </ListItem>
        ))}
        {/* Invisible anchor for auto-scroll */}
        <div ref={timelineEndRef} />
      </List>
    </div>
  );
};

export default AgentTimeline;
